import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Application from 'expo-application';
import { Platform } from 'react-native';

const DEVICE_ID_STORAGE_KEY = '@fimfam/internal/device-id';

/**
 * DeviceIdentifier - Singleton
 * Manages device identification using platform-specific expo-application APIs.
 * Caches device ID in memory and persists to AsyncStorage for faster subsequent access.
 */
export class DeviceIdentifier {
  private static instance: DeviceIdentifier;
  private deviceId: string | null = null;
  private initializationPromise: Promise<string> | null = null;

  private constructor() {}

  /**
   * Get singleton instance
   */
  static getInstance(): DeviceIdentifier {
    if (!DeviceIdentifier.instance) {
      DeviceIdentifier.instance = new DeviceIdentifier();
    }
    return DeviceIdentifier.instance;
  }

  /**
   * Initialize and get the device ID
   * Uses cached value if available, otherwise fetches from storage or generates new
   */
  async getDeviceId(): Promise<string> {
    // Return cached value if available
    if (this.deviceId) {
      return this.deviceId;
    }

    // Prevent concurrent initialization
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.initializeDeviceId();
    return this.initializationPromise;
  }

  /**
   * Synchronous getter for already-initialized device ID
   * Returns null if not yet initialized
   */
  getDeviceIdSync(): string | null {
    return this.deviceId;
  }

  /**
   * Check if device ID has been initialized
   */
  isInitialized(): boolean {
    return this.deviceId !== null;
  }

  /**
   * Internal initialization logic
   */
  private async initializeDeviceId(): Promise<string> {
    try {
      // First, try to load from AsyncStorage (faster)
      const storedId = await AsyncStorage.getItem(DEVICE_ID_STORAGE_KEY);
      if (storedId) {
        this.deviceId = storedId;
        return storedId;
      }

      // Get platform-specific device ID
      const deviceId = await this.getPlatformDeviceId();

      if (deviceId) {
        this.deviceId = deviceId;
        // Persist to AsyncStorage for faster subsequent access
        await AsyncStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
        return deviceId;
      }

      // Generate a fallback ID if device ID is not available
      const fallbackId = this.generateFallbackId();
      this.deviceId = fallbackId;
      await AsyncStorage.setItem(DEVICE_ID_STORAGE_KEY, fallbackId);
      return fallbackId;
    } catch (error) {
      console.error('[DeviceIdentifier] Failed to initialize device ID:', error);
      // Generate a fallback ID on error
      const fallbackId = this.generateFallbackId();
      this.deviceId = fallbackId;
      return fallbackId;
    }
  }

  /**
   * Get platform-specific device ID
   * - iOS: Uses identifier for vendor (IDFV)
   * - Android: Uses ANDROID_ID from Settings.Secure
   * - Web: Returns null (will use fallback)
   */
  private async getPlatformDeviceId(): Promise<string | null> {
    try {
      if (Platform.OS === 'ios') {
        // iOS identifier for vendor - unique per device per vendor
        const iosId = await Application.getIosIdForVendorAsync();
        return iosId;
      } else if (Platform.OS === 'android') {
        // Android ID - unique per device per app-signing key per user
        const androidId = Application.getAndroidId();
        return androidId;
      }
      // Web and other platforms - return null to use fallback
      return null;
    } catch (error) {
      console.error('[DeviceIdentifier] Failed to get platform device ID:', error);
      return null;
    }
  }

  /**
   * Generate a fallback device ID when platform ID is unavailable
   */
  private generateFallbackId(): string {
    const timestamp = Date.now().toString(36);
    const randomPart = Math.random().toString(36).substring(2, 15);
    return `device_${timestamp}_${randomPart}`;
  }
}

/**
 * Export singleton instance for convenience
 */
export const deviceIdentifier = DeviceIdentifier.getInstance();
